import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { scopedChannel } from "@/lib/realtime";

/**
 * Shared, ref-counted realtime subscription.
 *
 * Several portal surfaces (sidebar badge, mobile nav, page body) mount the
 * same counter hook at once. Previously each mount opened its own channel and
 * ran its own query, which is what produced dozens of duplicate requests on a
 * single dashboard load. Here every mount with the same `key` reuses ONE
 * channel; the last unmount tears it down.
 */

type Entry = {
  channel: ReturnType<typeof scopedChannel>;
  listeners: Set<() => void>;
};

const registry = new Map<string, Entry>();

export interface SharedChannelTable {
  table: string;
  event?: "*" | "INSERT" | "UPDATE" | "DELETE";
  filter?: string;
}

export function useSharedChannel(
  key: string | null,
  scope: { kind: string; id: string } | null,
  tables: SharedChannelTable[],
  onChange: () => void,
) {
  useEffect(() => {
    if (!key || !scope) return;

    let entry = registry.get(key);
    if (!entry) {
      const channel = scopedChannel(scope as never, key);
      const listeners = new Set<() => void>();
      entry = { channel, listeners };
      registry.set(key, entry);
      const fanout = () => listeners.forEach((l) => l());
      for (const t of tables) {
        channel.on(
          "postgres_changes" as never,
          { event: t.event ?? "*", schema: "public", table: t.table, ...(t.filter ? { filter: t.filter } : {}) } as never,
          fanout as never,
        );
      }
      channel.subscribe();
    }

    const listener = () => onChange();
    entry.listeners.add(listener);

    return () => {
      const current = registry.get(key);
      if (!current) return;
      current.listeners.delete(listener);
      if (current.listeners.size === 0) {
        registry.delete(key);
        supabase.removeChannel(current.channel);
      }
    };
    // `onChange` is intentionally not a dependency: it is re-created every
    // render and would otherwise churn the subscription.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, scope?.kind, scope?.id]);
}
